import { useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowClockwise, Folders, PencilSimple, Plus, Trash, UploadSimple } from "@phosphor-icons/react";
import { codeCategoryUpdateSchema, getProductCategoryPrefixes, productUpdateSchema, type CodeCategoryNode } from "@longjie/shared";
import { ApiError, request } from "./api";
import { Button, EmptyState, Field, LoadError, Modal, StatusBadge } from "./components";
import { CodeCategoryTree, ProductCodeCategoryNote, type CodeCategoryProduct } from "./CodeCategoryBrowser";
import { CategoryImage } from "./CategoryImage";
import { publishCategoryImage } from "./product-image-sync";
import { ImageCompressionError, MAX_IMAGE_UPLOAD_BYTES, prepareImageForUpload } from "./image-compression";
import { publishCodeCategorySetting, useSynchronizedCodeCategories } from "./code-category-sync";
import "./categories.css";

type CategoryOption = { id: string; name: string; sortOrder?: number; active?: boolean };
type ManagedCategory = CategoryOption & { productCount?: number; activeProductCount?: number; skuCount?: number };
type Notify = (message: string, tone?: "success" | "error" | "info") => void;

function errorMessage(error: unknown, fallback: string) {
  return error instanceof ApiError ? error.message : fallback;
}

export function CategoriesAdmin({ isAdmin, canManageProducts = isAdmin, notify, settingsOnly = false, onChanged }: { isAdmin: boolean; canManageProducts?: boolean; notify: Notify; settingsOnly?: boolean; onChanged?: () => void }) {
  const [categories, setCategories] = useState<ManagedCategory[]>([]);
  const [categoryNodes, setCodeCategories] = useState<CodeCategoryNode[] | undefined>();
  const codeCategories = useSynchronizedCodeCategories(categoryNodes);
  const [codeCategoryProducts, setCodeCategoryProducts] = useState<CodeCategoryProduct[] | undefined>();
  const [unclassifiedProductCount, setUnclassifiedProductCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [reload, setReload] = useState(0);
  const [editor, setEditor] = useState<ManagedCategory | "NEW" | null>(null);
  const [codeEditor, setCodeEditor] = useState<CodeCategoryNode | null>(null);
  const [productEditor, setProductEditor] = useState<CodeCategoryProduct | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);
  const updatingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    void request<{ categories: ManagedCategory[]; codeCategories?: CodeCategoryNode[]; codeCategoryProducts?: CodeCategoryProduct[]; unclassifiedProductCount?: number }>("/api/v1/admin/categories")
      .then((result) => { if (!cancelled) {
        setCategories(result.categories); setCodeCategories(result.codeCategories); setCodeCategoryProducts(result.codeCategoryProducts); setUnclassifiedProductCount(result.unclassifiedProductCount ?? 0);
      } })
      .catch((caught: unknown) => { if (!cancelled) setError(errorMessage(caught, "分类加载失败")); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reload]);

  function refresh() {
    setLoading(true); setError(""); setReload((value) => value + 1);
  }

  async function toggle(category: ManagedCategory) {
    if (!isAdmin || updatingRef.current) return;
    updatingRef.current = true;
    setUpdating(category.id); setActionError("");
    const active = category.active === false;
    try {
      await request(`/api/v1/admin/categories/${category.id}`, { method: "PATCH", body: JSON.stringify({ active }) });
      notify(`「${category.name}」已${active ? "启用" : "停用"}`);
      onChanged?.();
      refresh();
    } catch (caught) {
      setActionError(!active && caught instanceof ApiError && caught.status === 409
        ? "该分类还有启用的商品。请先在商品目录中把这些商品转移到其他分类，或停用这些商品，再停用分类。"
        : errorMessage(caught, "分类状态修改失败，请重试"));
    } finally { updatingRef.current = false; setUpdating(null); }
  }

  const categoryList = categories.length === 0
    ? <EmptyState title={codeCategories === undefined ? "还没有商品分类" : "还没有分类名称分组"} description={codeCategories === undefined ? "先新增分类，再在新建商品时选择所属分类。" : "可在这里新增分类名称，再在商品中维护名称分组。"} />
    : <div className="categories-list" aria-label="商品分类列表">
      {categories.map((category) => <article className="category-admin-row" key={category.id}>
        <span className="category-admin-order" aria-label={`显示顺序 ${category.sortOrder ?? 0}`}>{category.sortOrder ?? 0}</span>
        <div className="category-admin-info"><h3>{category.name}</h3><p>
          {category.productCount === undefined ? "" : `${category.productCount} 个商品`}
          {codeCategories !== undefined || category.skuCount === undefined ? "" : ` · ${category.skuCount} 个 SKU`}
          {codeCategories !== undefined || category.activeProductCount === undefined ? "" : ` · ${category.activeProductCount} 个启用商品`}
        </p></div>
        <StatusBadge tone={category.active === false ? "neutral" : "success"}>{category.active === false ? "已停用" : "已启用"}</StatusBadge>
        {isAdmin && <div className="category-admin-actions">
          <Button variant="secondary" disabled={updating !== null} aria-label={`编辑分类 ${category.name}`} onClick={() => setEditor(category)}><PencilSimple />编辑</Button>
          <Button variant="ghost" loading={updating === category.id} disabled={updating !== null} aria-label={`${category.active === false ? "启用" : "停用"}分类 ${category.name}`}
            onClick={() => void toggle(category)}>{category.active === false ? "启用" : "停用"}</Button>
        </div>}
      </article>)}
    </div>;
  const hasCodeCategories = codeCategories !== undefined && !settingsOnly;
  return <>
    <section className="admin-intro categories-intro">
      <div><p className="eyebrow">商品分类</p><h2>{settingsOnly ? "分类名称分组维护" : hasCodeCategories ? "按分类查看商品和库存" : "分类由你安排，商品清楚归位"}</h2>
        <p>{hasCodeCategories ? "商品编码每两位一段，末两位是商品序号。展开分类，查看完整商品编码、名称和实际库存。" : "可新增、改名和调整显示顺序。SKU 跟随所属商品的分类，录入商品时选择即可。"}</p></div>
      {isAdmin && !hasCodeCategories && <Button onClick={() => setEditor("NEW")}><Plus />新增分类</Button>}
    </section>
    <div className="categories-note"><Folders /><p>{hasCodeCategories
      ? "分类显示商品种数，库存显示实际数量；上级已包含下级，不能重复相加。不同单位的库存分开显示。"
      : "显示顺序数字越小，客户选分类时越靠前。同序分类按名称排列。停用前需先转移或停用该分类里的启用商品，分类记录会保留。"}</p></div>
    <div className="admin-toolbar categories-toolbar"><span>{hasCodeCategories ? `共 ${codeCategories.filter((node) => node.parentPrefix === null).reduce((total, node) => total + node.productCount, 0)} 种商品 · ${codeCategories.filter((node) => node.parentPrefix === null).length} 个大类` : `共 ${categories.length} 个分类`}</span>
      <Button variant="secondary" disabled={loading} onClick={refresh}><ArrowClockwise />刷新</Button></div>
    {actionError && <div className="inline-error" role="alert">{actionError}</div>}
    {loading ? <div className="loading-block" role="status">正在加载分类…</div>
      : error ? <LoadError title="分类暂时无法加载" description={error} onRetry={refresh} />
        : hasCodeCategories ? <>
          {codeCategories.length ? <CodeCategoryTree nodes={codeCategories} isAdmin={isAdmin || canManageProducts}
            {...(isAdmin ? { onEditCategory: setCodeEditor } : {})}
            {...(canManageProducts ? { onEditProduct: setProductEditor } : {})}
            {...(codeCategoryProducts === undefined ? {} : { products: codeCategoryProducts })} /> : <EmptyState title="还没有可识别的编码分类" description="输入每段两位、含分类和商品序号的数字商品编码后，会自动显示分类层级。" />}
          {unclassifiedProductCount > 0 && <p className="readonly-note">另有 {unclassifiedProductCount} 个商品编码不符合两位一段的规则，保留原分类名称分组，可在商品目录中查看。</p>}
          <details className="category-maintenance"><summary>分类名称维护（{categories.length} 个名称分组）</summary>
            <p>这里维护原有分类名称、显示顺序和启用状态。按编码分类树由商品编码自动生成；名称分组的调整不会改变编码路径。</p>
            {isAdmin && <Button onClick={() => setEditor("NEW")}><Plus />新增分类名称</Button>}
            {categoryList}
          </details>
        </> : categoryList}
    {!isAdmin && <div className="readonly-note">当前账号可以查看分类，但没有新增、修改或停用分类权限。</div>}
    {isAdmin && editor && <CategoryEditor {...(editor === "NEW" ? {} : { category: editor })} onClose={() => setEditor(null)} onSaved={() => {
      notify(editor === "NEW" ? "分类已新增" : "分类已更新"); setEditor(null); setActionError(""); refresh(); onChanged?.();
    }} />}
    {isAdmin && codeEditor && <CodeCategoryEditor category={codeEditor} onClose={() => setCodeEditor(null)} onSaved={() => {
      notify("分类名称和显示顺序已保存"); setCodeEditor(null); refresh(); onChanged?.();
    }} />}
    {canManageProducts && productEditor && <CategoryProductEditor product={productEditor} onClose={() => setProductEditor(null)} onSaved={() => {
      notify("商品已更新，分类已按编码重新识别"); setProductEditor(null); refresh(); onChanged?.();
    }} />}
  </>;
}

export function CodeCategoryEditor({ category, onClose, onSaved }: { category: CodeCategoryNode; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(category.name ?? "");
  const [sortOrder, setSortOrder] = useState(String(category.sortOrder ?? Number(category.segment) * 10));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [imageUrl, setImageUrl] = useState(category.imageUrl ?? null);
  const [imagePending, setImagePending] = useState<"compress" | "upload" | "delete" | null>(null);
  const [imageError, setImageError] = useState("");
  const [imageMessage, setImageMessage] = useState("");
  const imageBusy = useRef(false);
  const imageInput = useRef<HTMLInputElement>(null);
  const busy = saving || imagePending !== null;

  async function saveImage(file?: File) {
    if (savingRef.current || imageBusy.current) return;
    setImageError(""); setImageMessage("");
    imageBusy.current = true;
    setImagePending(file ? file.size > MAX_IMAGE_UPLOAD_BYTES ? "compress" : "upload" : "delete");
    try {
      const preparedFile = file ? await prepareImageForUpload(file) : undefined;
      const form = preparedFile ? new FormData() : undefined;
      if (preparedFile) { form!.append("file", preparedFile); setImagePending("upload"); }
      const result = await request<{ imageUrl: string | null }>(`/api/v1/admin/code-categories/${category.prefix}/image`, {
        method: file ? "POST" : "DELETE", ...(form ? { body: form } : {}),
      });
      setImageUrl(result.imageUrl);
      publishCategoryImage({ prefix: category.prefix, imageUrl: result.imageUrl });
      setImageMessage(file ? file.size > MAX_IMAGE_UPLOAD_BYTES ? "分类照片已压缩至 200 KB 以下并保存，各页面会同步显示。" : "分类照片已保存，各页面会同步显示。" : "分类照片已删除，各页面会同步更新。");
    } catch (caught) { setImageError(caught instanceof ImageCompressionError ? caught.message : errorMessage(caught, "分类照片更新失败，请重试。")); }
    finally { imageBusy.current = false; setImagePending(null); }
  }

  function close() {
    if (!savingRef.current && !imageBusy.current) onClose();
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (savingRef.current || imageBusy.current) return;
    const parsed = codeCategoryUpdateSchema.safeParse({ name, sortOrder: /^\d+$/.test(sortOrder) ? Number(sortOrder) : -1 });
    if (!parsed.success) { setError(parsed.error.issues[0]?.message ?? "请检查分类名称和显示顺序"); return; }
    savingRef.current = true; setSaving(true); setError("");
    try {
      await request(`/api/v1/admin/code-categories/${category.prefix}`, { method: "PATCH", body: JSON.stringify(parsed.data) });
      publishCodeCategorySetting({ prefix: category.prefix, ...parsed.data });
      onSaved();
    } catch (caught) { setError(errorMessage(caught, "分类保存失败，已保留填写内容，请重试")); }
    finally { savingRef.current = false; setSaving(false); }
  }
  return <Modal title="编辑编码分类" description={`分类路径 ${category.path} · 分类前缀 ${category.prefix}`}
    onClose={close} footer={<>
      <Button variant="secondary" disabled={busy} onClick={close}>关闭</Button>
      <Button form="code-category-editor" type="submit" loading={saving} disabled={imagePending !== null}>保存分类</Button>
    </>}>
    <form id="code-category-editor" className="category-form" onSubmit={(event) => void submit(event)}>
      {error && <div className="inline-error" role="alert">{error}</div>}
      <Field label="分类名称"><input value={name} onChange={(event) => setName(event.target.value)} required maxLength={80} disabled={busy} placeholder="填写这一级分类的名称" /></Field>
      <Field label="显示顺序" hint="同一上级内，数字越小越靠前。"><input type="number" min={0} max={99_999} step={1} required disabled={busy}
        value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} /></Field>
      <section className="category-image-editor" aria-label="分类代表照片">
        <div className="category-image-editor__preview"><CategoryImage prefix={category.prefix} imageUrl={imageUrl} alt={`${category.label}分类照片`} /></div>
        <div className="category-image-editor__content"><h3>分类代表照片</h3>
          <p>为大类或下级分类放一张代表照片，客户可以看图找类别。</p>
          <p className="field__hint">支持 JPG、PNG、WebP。超过 200 KB 自动压缩至 200 KB 以下，上传或删除后立即保存。</p>
          <input ref={imageInput} type="file" hidden aria-label="选择分类照片" accept="image/jpeg,image/png,image/webp" disabled={busy}
            onChange={(event) => { const file = event.currentTarget.files?.[0]; event.currentTarget.value = ""; if (file) void saveImage(file); }} />
          <div className="category-image-editor__actions">
            <Button type="button" variant="secondary" disabled={busy} onClick={() => imageInput.current?.click()}><UploadSimple />
              {imagePending === "compress" ? "压缩中…" : imagePending === "upload" ? "上传中…" : imageUrl ? "替换分类照片" : "上传分类照片"}</Button>
            {imageUrl && <Button type="button" variant="danger" disabled={busy} onClick={() => void saveImage()}><Trash />
              {imagePending === "delete" ? "删除中…" : "删除分类照片"}</Button>}
          </div>
          {imageError && <p className="field__error" role="alert">{imageError}</p>}
          {imageMessage && <p className="field__hint" role="status">{imageMessage}</p>}
        </div>
      </section>
      <p className="field__hint">自动提取的名称可在这里修改。手动保存后以此名称为准，商品目录、成品仓库和内外部下单分类都会同步更新。</p>
    </form>
  </Modal>;
}

export function CategoryProductEditor({ product, onClose, onSaved }: { product: CodeCategoryProduct; onClose: () => void; onSaved: () => void }) {
  const [code, setCode] = useState(product.code);
  const [name, setName] = useState(product.name);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (savingRef.current) return;
    const parsed = productUpdateSchema.safeParse({ code, name });
    if (!parsed.success) { setError(parsed.error.issues[0]?.message ?? "请检查商品编码和名称"); return; }
    if (/^[0-9]+$/.test(code.trim()) && !getProductCategoryPrefixes(code.trim()).length) {
      setError("数字商品编码应每两位一段，包含分类及末尾两位商品序号。"); return;
    }
    savingRef.current = true; setSaving(true); setError("");
    try {
      await request(`/api/v1/admin/products/${product.id}`, { method: "PATCH", body: JSON.stringify(parsed.data) });
      onSaved();
    } catch (caught) { setError(errorMessage(caught, "商品保存失败，已保留填写内容，请重试")); }
    finally { savingRef.current = false; setSaving(false); }
  }
  return <Modal title="编辑商品" description="修改商品编码和名称，分类会按保存后的编码重新识别。"
    onClose={() => { if (!savingRef.current) onClose(); }} footer={<>
      <Button variant="secondary" disabled={saving} onClick={onClose}>取消</Button>
      <Button form="category-product-editor" type="submit" loading={saving}>保存商品</Button>
    </>}>
    <form id="category-product-editor" className="category-form" onSubmit={(event) => void submit(event)}>
      {error && <div className="inline-error" role="alert">{error}</div>}
      <Field label="商品编码"><input value={code} onChange={(event) => setCode(event.target.value)} maxLength={50} required disabled={saving} /></Field>
      <Field label="商品名称"><input value={name} onChange={(event) => setName(event.target.value)} maxLength={160} required disabled={saving} /></Field>
      <ProductCodeCategoryNote code={code} />
      <p className="field__hint">库存由“成品仓库”中的采购入库、销售出库和盘点单据维护。</p>
    </form>
  </Modal>;
}

function CategoryEditor({ category, onClose, onSaved }: { category?: ManagedCategory; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(category?.name ?? "");
  const [sortOrder, setSortOrder] = useState(String(category?.sortOrder ?? 0));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (savingRef.current) return;
    setError("");
    if (!name.trim()) { setError("请填写分类名称。"); return; }
    if (!/^\d+$/.test(sortOrder) || Number(sortOrder) > 99_999) { setError("显示顺序应为 0 到 99999 的整数。"); return; }
    savingRef.current = true; setSaving(true);
    try {
      await request(category ? `/api/v1/admin/categories/${category.id}` : "/api/v1/admin/categories", {
        method: category ? "PATCH" : "POST",
        body: JSON.stringify({ name: name.trim(), sortOrder: Number(sortOrder), ...(category ? {} : { active: true }) }),
      });
      onSaved();
    } catch (caught) { setError(errorMessage(caught, "保存失败，已保留填写内容，请重试")); }
    finally { savingRef.current = false; setSaving(false); }
  }
  return <Modal title={category ? "编辑商品分类" : "新增商品分类"} description="维护分类名称分组及显示顺序。数字商品的多级分类由完整商品编码识别。"
    onClose={() => { if (!savingRef.current) onClose(); }} footer={<>
      <Button variant="secondary" onClick={onClose} disabled={saving}>取消</Button><Button form="category-editor" type="submit" loading={saving}>保存分类</Button>
    </>}>
    <form id="category-editor" className="category-form" onSubmit={(event) => void submit(event)}>
      {error && <div className="inline-error" role="alert">{error}</div>}
      <Field label="分类名称"><input value={name} onChange={(event) => setName(event.target.value)} required maxLength={80} disabled={saving} placeholder="例如：建材类、水电类" /></Field>
      <Field label="显示顺序" hint="例如 10、20、30；数字越小越靠前。"><input aria-label="显示顺序" type="number" min={0} max={99_999} step={1} required
        value={sortOrder} onChange={(event) => setSortOrder(event.target.value)} disabled={saving} /></Field>
    </form>
  </Modal>;
}

export function ProductCategoryEditor({ product, categories, onClose, onSaved }: {
  product: { id: string; name: string; code?: string; categoryId?: string; categoryName?: string };
  categories: CategoryOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [categoryId, setCategoryId] = useState(product.categoryId ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const savingRef = useRef(false);
  const hasCodeCategory = getProductCategoryPrefixes(product.code ?? "").length > 0;
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (savingRef.current) return;
    if (!categoryId) { setError("请选择商品所属分类。"); return; }
    savingRef.current = true; setSaving(true); setError("");
    try {
      await request(`/api/v1/admin/products/${product.id}`, { method: "PATCH", body: JSON.stringify({ categoryId }) });
      onSaved();
    } catch (caught) { setError(errorMessage(caught, "修改分类失败，请重试")); }
    finally { savingRef.current = false; setSaving(false); }
  }
  return <Modal title={hasCodeCategory ? "维护商品分类名称分组" : "修改商品分类"} description={hasCodeCategory ? `${product.name} · 维护分类名称分组，商品编码决定的分类路径保持不变。` : `${product.name} · 修改后，该商品的所有 SKU 一起归入新分类。`}
    onClose={() => { if (!savingRef.current) onClose(); }} footer={<>
      <Button variant="secondary" disabled={saving} onClick={onClose}>取消</Button>
      <Button form="product-category-editor" type="submit" loading={saving}>{hasCodeCategory ? "保存名称分组" : "保存商品分类"}</Button>
    </>}>
    <form id="product-category-editor" className="category-form" onSubmit={(event) => void submit(event)}>
      {error && <div className="inline-error" role="alert">{error}</div>}
      {hasCodeCategory && <><ProductCodeCategoryNote code={product.code ?? ""} /><p className="field__hint">编码分类由完整商品编码决定。下方维护商品的分类名称分组，不会改变按编码识别的层级。</p></>}
      <p>{hasCodeCategory ? "当前名称分组" : "当前分类"}：{product.categoryName ?? "未分类"}</p>
      <Field label={hasCodeCategory ? "商品分类名称分组" : "商品所属分类"}><select value={categoryId} required disabled={saving} onChange={(event) => setCategoryId(event.target.value)}>
        <option value="">请选择分类</option>
        {categories.filter((category) => category.active !== false || category.id === product.categoryId).map((category) =>
          <option value={category.id} key={category.id} disabled={category.active === false}>{category.name}{category.active === false ? "（已停用）" : ""}</option>)}
      </select></Field>
      <p className="field__hint">{hasCodeCategory ? "名称分组可在“分类管理”的“分类名称维护”里新增或调整。" : "这里修改商品及其全部规格的归属。分类可在“分类管理”里新增或调整。"}</p>
    </form>
  </Modal>;
}
